import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  saveCard: (cardId: string) => void;
  removeSavedCard: (cardId: string) => void;
  createWatchlist: (name: string) => void;
  addToWatchlist: (watchlistId: string, cardId: string) => void;
  removeFromWatchlist: (watchlistId: string, cardId: string) => void;
  isCardSaved: (cardId: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Load user from localStorage on app start
    const savedUser = localStorage.getItem('goatCardsUser');
    const savedAdminStatus = localStorage.getItem('goatCardsAdmin');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    if (savedAdminStatus) {
      setIsAdmin(JSON.parse(savedAdminStatus));
    }
  }, []);

  const saveUserToStorage = (userData: User, adminStatus: boolean = false) => {
    localStorage.setItem('goatCardsUser', JSON.stringify(userData));
    localStorage.setItem('goatCardsAdmin', JSON.stringify(adminStatus));
  };

  const login = async (email: string, password: string) => {
    // Check for admin credentials
    if (email === 'admin@goatcards.shop' && password === 'admin123') {
      const adminUser: User = {
        id: 'admin',
        email,
        savedCards: [],
        watchlists: [],
      };
      setUser(adminUser);
      setIsAdmin(true);
      saveUserToStorage(adminUser, true);
      return;
    }

    // Check registered users
    const existingUsers = JSON.parse(localStorage.getItem('goatCardsUsers') || '[]');
    const user = existingUsers.find((u: any) => u.email === email && u.password === password);
    
    if (!user) {
      throw new Error('Invalid email or password');
    }

    // Create user object for login
    const loginUser: User = {
      id: Date.now().toString(), // Generate new ID for session
      email,
      savedCards: [],
      watchlists: [],
    };

    setUser(loginUser);
    setIsAdmin(false);
    saveUserToStorage(loginUser, false);
  };

  const register = async (email: string, password: string) => {
    // Check if user already exists (simple localStorage check)
    const existingUsers = JSON.parse(localStorage.getItem('goatCardsUsers') || '[]');
    const userExists = existingUsers.find((user: any) => user.email === email);
    
    if (userExists) {
      throw new Error('User with this email already exists');
    }

    // Create new user
    const newUser: User = {
      id: Date.now().toString(),
      email,
      savedCards: [],
      watchlists: [],
    };

    // Save to users list
    const updatedUsers = [...existingUsers, { email, password }];
    localStorage.setItem('goatCardsUsers', JSON.stringify(updatedUsers));

    // Set as current user
    setUser(newUser);
    setIsAdmin(false);
    saveUserToStorage(newUser, false);
  };

  const logout = () => {
    setUser(null);
    setIsAdmin(false);
    localStorage.removeItem('goatCardsUser');
    localStorage.removeItem('goatCardsAdmin');
  };

  const saveCard = (cardId: string) => {
    if (user) {
      const updatedUser = {
        ...user,
        savedCards: [...user.savedCards, cardId],
      };
      setUser(updatedUser);
      saveUserToStorage(updatedUser, isAdmin);
    }
  };

  const removeSavedCard = (cardId: string) => {
    if (user) {
      const updatedUser = {
        ...user,
        savedCards: user.savedCards.filter(id => id !== cardId),
      };
      setUser(updatedUser);
      saveUserToStorage(updatedUser, isAdmin);
    }
  };

  const createWatchlist = (name: string) => {
    if (user) {
      const newWatchlist = {
        id: Date.now().toString(),
        name,
        cardIds: [],
        createdAt: new Date(),
      };
      const updatedUser = {
        ...user,
        watchlists: [...user.watchlists, newWatchlist],
      };
      setUser(updatedUser);
      saveUserToStorage(updatedUser, isAdmin);
    }
  };

  const addToWatchlist = (watchlistId: string, cardId: string) => {
    if (user) {
      const updatedWatchlists = user.watchlists.map(watchlist =>
        watchlist.id === watchlistId
          ? { ...watchlist, cardIds: [...watchlist.cardIds, cardId] }
          : watchlist
      );
      const updatedUser = {
        ...user,
        watchlists: updatedWatchlists,
      };
      setUser(updatedUser);
      saveUserToStorage(updatedUser, isAdmin);
    }
  };

  const removeFromWatchlist = (watchlistId: string, cardId: string) => {
    if (user) {
      const updatedWatchlists = user.watchlists.map(watchlist =>
        watchlist.id === watchlistId
          ? { ...watchlist, cardIds: watchlist.cardIds.filter(id => id !== cardId) }
          : watchlist
      );
      const updatedUser = {
        ...user,
        watchlists: updatedWatchlists,
      };
      setUser(updatedUser);
      saveUserToStorage(updatedUser, isAdmin);
    }
  };

  const isCardSaved = (cardId: string): boolean => {
    return user?.savedCards.includes(cardId) || false;
  };

  const value: AuthContextType = {
    user,
    isAdmin,
    login,
    register,
    logout,
    saveCard,
    removeSavedCard,
    createWatchlist,
    addToWatchlist,
    removeFromWatchlist,
    isCardSaved,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider; 